<template>
  <div class="bubble-shooter flex flex-col h-full w-full items-center justify-center">
    <div class="relative rounded-2xl overflow-hidden bg-[#1c4848]/5 border-2 border-[#1c4848]/10" :style="arenaStyle">
      <div class="grid gap-0.5 p-1 absolute top-0 left-0 right-0" :style="gridStyle">
        <div
          v-for="(cell, idx) in grid"
          :key="'b-' + idx"
          class="bubble-cell rounded-full border border-white/20"
          :style="cell ? { backgroundColor: COLORS[cell.color % COLORS.length] } : {}"
        />
      </div>
      <div
        v-if="shootingBubble"
        class="shooting-bubble absolute rounded-full border-2 border-white/30 shadow-lg"
        :style="shootingStyle"
      />
      <div
        class="cannon absolute bottom-2 left-1/2 -translate-x-1/2 w-12 h-12 flex items-center justify-center"
        :style="{ transform: `translate(-50%, 0) rotate(${angleDeg}deg)` }"
      >
        <div
          class="next-bubble w-10 h-10 rounded-full border-2 border-white/30"
          :style="nextBubbleStyle"
        />
      </div>
    </div>
    <div class="absolute bottom-4 left-1/2 -translate-x-1/2 w-64 h-16 pointer-events-none" />
    <canvas
      ref="aimRef"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 w-64 h-48 cursor-crosshair pointer-events-auto"
      width="256"
      height="192"
      @click="onShoot"
      @mousemove="onAim"
      @touchmove.prevent="onAimTouch"
      @touchstart.prevent="onAimTouch"
    />
    <p class="mt-4 text-sm text-[#1c4848]/60">Click to shoot. Match 3+ to clear. Clear the board to win.</p>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { parseBubbleConfig } from './parseGameConfig.js';

const COLORS = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#0097a7', '#fdd835', '#5d4037'];

const props = defineProps({ game: { type: Object, required: true } });
const emit = defineEmits(['win', 'stats', 'gameover']);

const config = computed(() => parseBubbleConfig(props.game?.config));
const cols = computed(() => config.value.gridCols);
const rows = computed(() => 8);
const cellSize = 28;
const grid = ref([]);
const score = ref(0);
const nextColor = ref(0);
const shootingBubble = ref(null);
const angleRad = ref(0);
const aimRef = ref(null);
const gameOver = ref(false);
const won = ref(false);

const arenaStyle = computed(() => ({
  width: cols.value * cellSize + 24 + 'px',
  height: rows.value * cellSize + 80 + 'px',
}));

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${cols.value}, ${cellSize}px)`,
  width: cols.value * cellSize + 'px',
}));

const angleDeg = computed(() => (angleRad.value * 180 / Math.PI));

const shootingStyle = computed(() => {
  const s = shootingBubble.value;
  if (!s) return {};
  return {
    width: cellSize - 4 + 'px',
    height: cellSize - 4 + 'px',
    left: s.x + 'px',
    top: s.y + 'px',
    backgroundColor: COLORS[s.color % COLORS.length],
    marginLeft: -(cellSize - 4) / 2 + 'px',
    marginTop: -(cellSize - 4) / 2 + 'px',
  };
});

const nextBubbleStyle = computed(() => ({
  backgroundColor: COLORS[nextColor.value % COLORS.length],
}));

function getColor() {
  return Math.floor(Math.random() * config.value.colorsCount);
}

function indexToRowCol(i) {
  const c = cols.value;
  return { r: Math.floor(i / c), col: i % c };
}

function rowColToIndex(r, col) {
  if (col < 0 || col >= cols.value) return -1;
  return r * cols.value + col;
}

function getConnectedSameColor(arr, startIdx) {
  const c = cols.value;
  const color = arr[startIdx]?.color;
  if (color == null) return new Set();
  const set = new Set();
  const stack = [startIdx];
  while (stack.length) {
    const i = stack.pop();
    if (set.has(i)) continue;
    if (arr[i]?.color !== color) continue;
    set.add(i);
    const { r, col } = indexToRowCol(i);
    [
      rowColToIndex(r - 1, col),
      rowColToIndex(r + 1, col),
      rowColToIndex(r, col - 1),
      rowColToIndex(r, col + 1),
      rowColToIndex(r - 1, col + (r % 2)),
      rowColToIndex(r + 1, col + (r % 2)),
    ].filter((idx) => idx >= 0 && idx < arr.length).forEach((idx) => stack.push(idx));
  }
  return set;
}

function dropFloating(arr) {
  const c = cols.value;
  const r = rows.value;
  const visited = new Set();
  const stack = [];
  for (let col = 0; col < c; col++) {
    const idx = rowColToIndex(0, col);
    if (arr[idx]) stack.push(idx);
  }
  while (stack.length) {
    const i = stack.pop();
    if (visited.has(i)) continue;
    visited.add(i);
    const { r, col } = indexToRowCol(i);
    [
      rowColToIndex(r - 1, col),
      rowColToIndex(r + 1, col),
      rowColToIndex(r, col - 1),
      rowColToIndex(r, col + 1),
      rowColToIndex(r - 1, col + (r % 2)),
      rowColToIndex(r + 1, col + (r % 2)),
    ].filter((idx) => idx >= 0 && idx < arr.length && arr[idx]).forEach((idx) => stack.push(idx));
  }
  const next = [...arr];
  for (let i = 0; i < next.length; i++) {
    if (next[i] && !visited.has(i)) next[i] = null;
  }
  for (let col = 0; col < c; col++) {
    const column = [];
    for (let row = 0; row < r; row++) {
      const idx = rowColToIndex(row, col);
      if (next[idx]) column.push(next[idx]);
    }
    const numNulls = r - column.length;
    for (let row = 0; row < r; row++) next[row * c + col] = row < numNulls ? null : column[row - numNulls];
  }
  return next;
}

function snapToGrid(px, py) {
  const c = cols.value;
  const r = rows.value;
  const ox = 12 + cellSize / 2;
  const oy = 12 + cellSize / 2;
  const col = Math.round((px - ox) / (cellSize * 0.9));
  const row = Math.round((py - oy) / (cellSize * 1.05));
  const colClamp = Math.max(0, Math.min(c - 1, col));
  const rowClamp = Math.max(0, Math.min(r - 1, row));
  return rowClamp * c + colClamp;
}

function onAim(e) {
  const canvas = aimRef.value;
  if (!canvas) return;
  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  const cy = rect.height / 2;
  const cx = rect.width / 2;
  angleRad.value = Math.atan2(cx - x, cy - y) - Math.PI / 2;
}

function onAimTouch(e) {
  if (e.touches?.length) {
    const t = e.touches[0];
    const canvas = aimRef.value;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    onAim({ clientX: t.clientX, clientY: t.clientY });
  }
}

function onShoot(e) {
  if (gameOver.value || shootingBubble.value) return;
  const canvas = aimRef.value;
  if (!canvas) return;
  const rect = canvas.getBoundingClientRect();
  const x = e.offsetX ?? e.clientX - rect.left;
  const y = e.offsetY ?? e.clientY - rect.top;
  onAim(e);
  const color = nextColor.value;
  nextColor.value = getColor();
  const startX = cols.value * cellSize / 2 + 12;
  const startY = rows.value * cellSize + 12 + 20;
  const dx = Math.sin(angleRad.value) * 8;
  const dy = -Math.cos(angleRad.value) * 8;
  let px = startX;
  let py = startY;
  shootingBubble.value = { x: px, y: py, color };
  const animate = () => {
    px += dx;
    py += dy;
    shootingBubble.value = { x: px, y: py, color };
    if (py < 12 + cellSize) {
      let idx = snapToGrid(px, py);
      shootingBubble.value = null;
      const arr = [...grid.value];
      const c = cols.value;
      const r = rows.value;
      if (arr[idx]) {
        const col = idx % c;
        for (let row = 0; row < r; row++) {
          const i = row * c + col;
          if (!arr[i]) { idx = i; break; }
        }
      }
      if (idx < 0 || idx >= arr.length || arr[idx]) return;
      arr[idx] = { color };
      const connected = getConnectedSameColor(arr, idx);
      if (connected.size >= config.value.minMatch) {
        connected.forEach((i) => { arr[i] = null; });
        score.value += connected.size * 10;
        grid.value = dropFloating(arr);
      } else {
        grid.value = arr;
      }
      emit('stats', { score: score.value });
      checkWinLose();
      return;
    }
    if (px < 12 || px > cols.value * cellSize + 12) {
      shootingBubble.value = null;
      return;
    }
    requestAnimationFrame(animate);
  };
  requestAnimationFrame(animate);
}

function checkWinLose() {
  const arr = grid.value;
  const filled = arr.filter(Boolean).length;
  if (filled === 0) {
    won.value = true;
    gameOver.value = true;
    emit('win');
    return;
  }
  const c = cols.value;
  const r = rows.value;
  const lastRow = r - 1;
  for (let col = 0; col < c; col++) {
    if (arr[lastRow * c + col]) {
      gameOver.value = true;
      emit('gameover', { score: score.value });
      return;
    }
  }
}

function init() {
  const c = cols.value;
  const r = rows.value;
  const initRows = Math.min(config.value.initialRows, r);
  const total = c * r;
  const arr = Array(total).fill(null);
  for (let i = 0; i < total; i++) {
    const row = Math.floor(i / c);
    if (row < initRows) arr[i] = { color: getColor() };
  }
  grid.value = arr;
  score.value = 0;
  nextColor.value = getColor();
  shootingBubble.value = null;
  gameOver.value = false;
  won.value = false;
  angleRad.value = 0;
  emit('stats', { score: 0 });
}

watch(() => [props.game?.id, config.value.gridCols, config.value.initialRows], () => {
  if (props.game?.id) init();
}, { immediate: true });

defineExpose({ restart: init });
</script>

<style scoped>
.bubble-cell { width: 26px; height: 26px; }
</style>
